import React from "react";
import lodashGet from "lodash/get";
import withStyles from "@material-ui/styles/withStyles";
import { connect, useDispatch, useSelector } from "react-redux";
import { initialize, change } from "redux-form";
import moment from "moment";

import CircularProgress from "@material-ui/core/CircularProgress";
import ExcelIcon from "mdi-material-ui/FileExcel";
import Drawer from "@material-ui/core/Drawer";

import { MuiTable } from "@jazasoft/mui-table";

import {
  PageHeader,
  Filter,
  Button,
  showNotification,
  ReferenceArrayInput,
  SelectArrayInput,
  AutoCompleteArrayInput,
  ReferenceInput,
  SelectInput,
  DateInput,
  changeFilterParams,
  translate
} from "jazasoft";
import { getDistinctValues, isEmpty, toFixed, toFixedValue } from "../../../utils/helpers";
import { UOM } from "../../../constants";
import { useDeepCallback, useDeepMemo, useMutation, useQuery, useSetting, useTenant, useUnit } from "../../../hooks";
import SettingKeys from "../../../utils/SettingKeys";
import { AlertParemeter } from "../../../utils/types";

const headerStyle1 = { bold: true, bgColor: "19, 96, 164", color: "255,255,255" };
const headerStyle2 = { bold: true, bgColor: "0,98,5", color: "255,255,255" };

const defaultMonths = 3;

function getCustomWeekNumber({ dateInput, startMonth, weekStartDay }) {
  const date = new Date(dateInput);
  let year = date.getFullYear();

  if (date.getMonth() < Number(startMonth)) {
    year -= 1;
  }

  const startDate = new Date(year, Number(startMonth), 1);

  const startDateDay = startDate.getDay();

  const diffToWeekStart = (Number(weekStartDay) - startDateDay + 7) % 7;

  const firstFullWeekStart = new Date(startDate);
  firstFullWeekStart.setDate(startDate.getDate() + diffToWeekStart);

  if (date < firstFullWeekStart) {
    return 1;
  }

  const diffInDays = Math.floor((date - firstFullWeekStart) / (1000 * 60 * 60 * 24));

  return Math.floor(diffInDays / 7) + 2;
}

const inputOptions = {
  xs: 12,
  fullWidth: true,
  options: { fullWidth: true }
};

const filterDefaultValues = defaultMonths => ({
  orderCloseDate: {
    start: defaultMonths === 0 ? moment().startOf("day").valueOf() : moment().subtract(defaultMonths, "months").startOf("day").valueOf(),
    end: moment().endOf("day").valueOf()
  }
});

const getSearchText = filterValues => {
  const { start, end, productIds, ...values } = filterValues || {};
  let searchItems = [];
  if (start && end) {
    searchItems.push(`orderCloseDate=ge=${start}`);
    searchItems.push(`orderCloseDate=le=${end}`);
  }
  const items = Object.keys(values)
    .filter(key => !isEmpty(values[key]))
    .map(key => (Array.isArray(values[key]) ? `${key}=in=(${values[key].map(e => `'${e}'`).join(",")})` : `${key}=='${values[key]}'`));

  return [...searchItems, ...items].join(";");
};

const dateKey = "orderCloseDate";

const uomChoices = [
  { id: UOM.METER.value, name: UOM.METER.label },
  { id: UOM.KILOGRAM.value, name: UOM.KILOGRAM.label }
];

const filterKeys = ["start", "end", "fabricUom", "unitIds", "season", "product", "fabricPattern", "fabricCategory", "supplier", "customer"];

const initialValues = filterDefaultValues(defaultMonths)[dateKey];

const columns = [{ dataKey: "name", title: "Report Name", length: 100 }];

const styles = theme => ({
  root: {},
  content: {
    padding: theme.spacing(3)
  },
  drawerHeader: {
    width: 500,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    padding: theme.spacing(2)
  },
  displayCenter: {
    display: "flex",
    justifyContent: "center",
    paddingVertical: theme.spacing(3),
    paddingBottom: theme.spacing(3)
  }
});

const CommonReport = props => {
  const { classes } = props;
  const { unitChoices } = useUnit();
  const tenant = useTenant();

  const [reportId, setReportId] = React.useState(null);
  const [filterOpen, setFilterOpen] = React.useState(false);

  const financialYearStartMonth = useSetting(SettingKeys.FINANCIAL_YEAR_START_MONTH) || 0;
  const weekStartDay = useSetting(SettingKeys.WEEK_START_DAY) || 0;

  const [mutate, { loading: busy }] = useMutation();
  const [download, { loading }] = useMutation({ rawResponse: true });
  const dispatch = useDispatch();

  //** Custom Reports has to be added here */
  let reports = [{ id: "open-order", name: "Open Order Reconciliation Report" }];

  if (
    tenant?.tenantId === "aquarelle" ||
    tenant?.tenantId === "aqtest" ||
    tenant?.tenantId === "aq_lg" ||
    tenant?.tenantId === "aq_lg_test" ||
    tenant?.tenantId === "laguna_v5" ||
    tenant?.tenantId === "laguna_test"
  ) {
    reports = [...reports, { id: "ocr", name: "CCR Report" }];
  }

  const { data: customers = [] } = useQuery({ url: "customers" });
  const { data: products = [] } = useQuery({ url: "products" });
  const { data: seasons = [] } = useQuery({ url: "seasons" });
  const { data: suppliers = [] } = useQuery({ url: "suppliers" });
  const { data: fabricPatterns = [] } = useQuery({ url: "fabric-patterns" });
  const { data: fabricCategories = [] } = useQuery({ url: "fabric-categories" });
  const { data: alertParameterList = [] } = useQuery({ url: "alert-parameters", params: { size: 1000 } });

  const inlineActions = [{ name: "Apply Filter and Download", tooltip: "", icon: <ExcelIcon fontSize="medium" style={{ color: "green" }} /> }];

  const onInlineActionClick = (_, action, row) => {
    setFilterOpen(true);
    setReportId(row.id);
  };

  const filterValues =
    useSelector(state => state.form[`${reportId}-filter-form`] && state.form[`${reportId}-filter-form`])?.values || initialValues || {};
  const i18nMessages = useSelector(state => state.i18n?.messages || {});

  const excelColumns = useDeepMemo(() => {
    const cuAlertParam = alertParameterList?.find(e => e.parameter === AlertParemeter.CONSUMPTION_VARIATION.id);
    const cuVarReasonList = getDistinctValues(cuAlertParam?.alertParameterItems?.map(e => e.reason)?.filter(e => !isEmpty(e)) || []);

    let cols = [];
    if (reportId !== "ocr") {
      cols = [
        { dataKey: "unitIds", title: "Unit", headerStyle: headerStyle1 },
        { dataKey: "cuttingStartDate", title: "Cutting Start Date", type: "date", headerStyle: headerStyle1 },
        { dataKey: "orderCloseDate", title: "Close Date", type: "date", headerStyle: headerStyle1 },
        { dataKey: "poRef", title: "ERP No", headerStyle: headerStyle1 },
        { dataKey: "customer", title: "Buyer", headerStyle: headerStyle1 },
        { dataKey: "season", title: "Season", headerStyle: headerStyle1 },
        { dataKey: "style", title: "Style", headerStyle: headerStyle1 },
        { dataKey: "styleNo", title: "Style No", headerStyle: headerStyle1 },
        { dataKey: "color", title: "Style Color", headerStyle: headerStyle1 },
        { dataKey: "part", title: "Part", headerStyle: headerStyle1 },
        { dataKey: "placement", title: "Placement", headerStyle: headerStyle1 },

        { dataKey: "orderQty", title: "Order Qty", headerStyle: headerStyle2 },
        { dataKey: "fpoQty", title: "FPO Qty", headerStyle: headerStyle2 },
        { dataKey: "cutQty", title: "Cut Quantity", headerStyle: headerStyle2 },
        { dataKey: "rejectedQty", title: "Rejected Qty", headerStyle: headerStyle2 },
        { dataKey: "netCutPercent", title: "Net Cut (%)", cellStyle: { dataFormat: "0.0%" }, headerStyle: headerStyle2 },

        { dataKey: "orderingCu", title: "BOM CU", type: "double", cellStyle: { dataFormat: "0.000" }, headerStyle: headerStyle1 },
        { dataKey: "stdCu", title: "Std. CU", type: "double", cellStyle: { dataFormat: "0.000" }, headerStyle: headerStyle1 },
        { dataKey: "planCu", title: "Plan CU", type: "double", cellStyle: { dataFormat: "0.000" }, headerStyle: headerStyle1 },
        { dataKey: "actualCu", title: " Actual CU", type: "double", cellStyle: { dataFormat: "0.000" }, headerStyle: headerStyle1 },
        { dataKey: "grossCu", title: "Gross CU", cellStyle: { dataFormat: "0.000" }, headerStyle: headerStyle1 },
        { dataKey: "actualCuDiff", title: "BOM vs Actual", type: "double", cellStyle: { dataFormat: "0.0" }, headerStyle: headerStyle1 },
        { dataKey: "grossCuDiff", title: "BOM vs Gross", cellStyle: { dataFormat: "0.0" }, headerStyle: headerStyle1 },

        { dataKey: "markerEfficiency", title: "Marker Efficiency", cellStyle: { dataFormat: "0.00%" }, headerStyle: headerStyle2 },
        { dataKey: "fabDirection", title: "Fab Direction", headerStyle: headerStyle2 },
        { dataKey: "csv", title: "CSV", headerStyle: headerStyle2 },
        { dataKey: "fabricPattern", title: "Fab Pattern", headerStyle: headerStyle2 },
        { dataKey: "fabRepeat", title: "Fab Repeat", headerStyle: headerStyle2 },
        { dataKey: "orderingWidth", title: "Fab Bom Width (in CM)", headerStyle: headerStyle2 },
        { dataKey: "actualWidth", title: "Fab Actual Width (in CM)", headerStyle: headerStyle2 },
        { dataKey: "widthDiff", title: "Width Deviation", headerStyle: headerStyle2 },
        { dataKey: "actualWarpShrinkage", title: "Actual Warp Shrinkage", headerStyle: headerStyle2 },
        { dataKey: "actualWeftShrinkage", title: "Actual Weft Shrinkage", headerStyle: headerStyle2 },

        { dataKey: "fabricOrderQty", title: "Fabric Required Qty", headerStyle: headerStyle1 },
        { dataKey: "associationQty", title: "Fabric Associated", headerStyle: headerStyle1 },
        { dataKey: "allocated", title: "Fabric Allocated", headerStyle: headerStyle1 },
        { dataKey: "blanketQty", title: "Testing Loss", headerStyle: headerStyle1 },
        { dataKey: "issued", title: "Fabric Issued", headerStyle: headerStyle1 },
        { dataKey: "returned", title: "Returned", headerStyle: headerStyle1 },
        { dataKey: "leftover", title: "Gross Leftover", headerStyle: headerStyle1 },

        { dataKey: "netIssued", title: "Net Issued", headerStyle: headerStyle2 },
        { dataKey: "shortageExcess", title: "Shortage/Excess", headerStyle: headerStyle2 },
        { dataKey: "usedInMarker", title: "Used in Marker", headerStyle: headerStyle2 },
        { dataKey: "endLoss", title: "End Loss", headerStyle: headerStyle2 },
        { dataKey: "usedInLayManual", title: "Used in Recutting Lay", headerStyle: headerStyle2 },
        { dataKey: "grossEndBits", title: "Gross End Bits", headerStyle: headerStyle2 },
        { dataKey: "usedInPanelRemnant", title: "Panel Recut (End Bits)", headerStyle: headerStyle2 },
        { dataKey: "netEndBits", title: "Net End Bits", headerStyle: headerStyle2 },
        { dataKey: "usedInPanelRoll", title: "Panel Recut (Roll)", headerStyle: headerStyle2 },
        { dataKey: "usedInPanel", title: "Total Panel Recut", headerStyle: headerStyle2 },
        { dataKey: "damaged", title: "Fabric Damaged", headerStyle: headerStyle2 },
        { dataKey: "netWastage", title: "Net Wastage", headerStyle: headerStyle2 },
        { dataKey: "missing", title: "Missed", headerStyle: headerStyle2 },
        { dataKey: "unused", title: "Unused", headerStyle: headerStyle2 },
        { dataKey: "grossWastage", title: "Gross Wastage", headerStyle: headerStyle2 },

        { dataKey: "itemCode", title: "Item Code", headerStyle: headerStyle1 },
        { dataKey: "articleName", title: "Article Name", headerStyle: headerStyle1 },
        { dataKey: "fabColor", title: "Item Color", headerStyle: headerStyle1 },
        { dataKey: "supplier", title: "Supplier", headerStyle: headerStyle1 },

        { dataKey: "customerCode", title: "Customer Code", headerStyle: headerStyle2 },
        { dataKey: "merchant", title: "Merchandiser", headerStyle: headerStyle2 },
        { dataKey: "customerFlowRefs", title: "BPO", headerStyle: headerStyle2 },
        { dataKey: "orderQtySizeBreakup", title: "Order Qty Size Breakup", headerStyle: headerStyle2 },
        { dataKey: "cutQtySizeBreakup", title: "Cut Qty Size Breakup", headerStyle: headerStyle2 },
        { dataKey: "state", title: "State", headerStyle: headerStyle1 }
      ].map(c => ({ ...c, title: lodashGet(i18nMessages, `resources.report.arr.excelColumns.${c.dataKey}`) || c.title }));
    } else if (reportId === "ocr") {
      cols = [
        { dataKey: "region", title: "Region", headerStyle: headerStyle1 },
        { dataKey: "unitIds", title: "Unit", headerStyle: headerStyle1 },
        { dataKey: "cuttingStartDate", title: "Cutting Start Date", type: "date", headerStyle: headerStyle1 },
        { dataKey: "cuttingClosedDate", title: "Cutting End Date", type: "date", headerStyle: headerStyle1 },
        { dataKey: "month", title: "Month", headerStyle: headerStyle1 },
        { dataKey: "cuttingWeek", title: "Cutting Week", headerStyle: headerStyle1 },
        { dataKey: "orderCloseDate", title: "Close Date", type: "date", headerStyle: headerStyle1 },
        { dataKey: "styleNo", title: "Style No", headerStyle: headerStyle1 },
        { dataKey: "poRef", title: "ERP No", headerStyle: headerStyle1 },
        { dataKey: "customer", title: "Buyer", headerStyle: headerStyle1 },
        { dataKey: "customerCode", title: "Buyer Code", headerStyle: headerStyle1 },
        { dataKey: "style", title: "Style", headerStyle: headerStyle1 },
        { dataKey: "color", title: "Style Color", headerStyle: headerStyle1 },
        { dataKey: "customerFlowRefs", title: "BPO", headerStyle: headerStyle1 },
        { dataKey: "season", title: "Season", headerStyle: headerStyle1 },
        { dataKey: "product", title: "Product", headerStyle: headerStyle1 },
        { dataKey: "genders", title: "Gender", headerStyle: headerStyle1 },
        { dataKey: "sleeveTypes", title: "Sleeve Type", headerStyle: headerStyle1 },
        { dataKey: "cutPlanApprovedBy", title: "Merchandiser", headerStyle: headerStyle1 },
        { dataKey: "part", title: "Part", headerStyle: headerStyle1 },
        { dataKey: "placement", title: "Placement", headerStyle: headerStyle1 },

        { dataKey: "fabricPattern", title: "Fab Type", headerStyle: headerStyle2 },
        { dataKey: "fabRepeat", title: "Fab Repeat (CM)", headerStyle: headerStyle2 },
        { dataKey: "orderingCsv", title: "Order CSV Grade", headerStyle: headerStyle2 },
        { dataKey: "csv", title: "Actual CSV", headerStyle: headerStyle2 },
        { dataKey: "supplier", title: "Fabric Supplier", headerStyle: headerStyle2 },
        { dataKey: "nominated", title: "Nominated", headerStyle: headerStyle2 },
        { dataKey: "itemCode", title: "FAB Item Code", headerStyle: headerStyle2 },
        { dataKey: "articleName", title: "Article Name", headerStyle: headerStyle2 },
        { dataKey: "fabColor", title: "Item Color", headerStyle: headerStyle2 },

        { dataKey: "orderingWidth", title: "Fab Bom Width (in CM)", headerStyle: headerStyle2 },
        { dataKey: "actualWidth", title: "Fab Actual Width (in CM)", headerStyle: headerStyle2 },
        { dataKey: "widthDiff", title: "Width Deviation", headerStyle: headerStyle2 },
        { dataKey: "orderingWarpShrinkage", title: "Ordering Warp Shrinkage", headerStyle: headerStyle2 },
        { dataKey: "orderingWeftShrinkage", title: "Ordering Weft Shrinkage", headerStyle: headerStyle2 },
        { dataKey: "actualWarpShrinkage", title: "Actual Warp Shrinkage", headerStyle: headerStyle2 },
        { dataKey: "actualWeftShrinkage", title: "Actual Weft Shrinkage", headerStyle: headerStyle2 },
        { dataKey: "warpShrinkageDiff", title: "Shrinkage Difference Warp", headerStyle: headerStyle2 },
        { dataKey: "weftShrinkageDiff", title: "Shrinkage Difference Weft", headerStyle: headerStyle2 },
        { dataKey: "markerEfficiency", title: "Marker Efficiency", cellStyle: { dataFormat: "0.00%" }, headerStyle: headerStyle2 },
        { dataKey: "fabDirection", title: "Fab Direction", headerStyle: headerStyle2 },

        { dataKey: "fabricOrderQty", title: "Fabric Required Qty", headerStyle: headerStyle1 },
        { dataKey: "associationQty", title: "Fabric Associated", headerStyle: headerStyle1 },
        { dataKey: "allocated", title: "Fabric Allocated", headerStyle: headerStyle1 },
        { dataKey: "blanketQty", title: "Testing Loss", headerStyle: headerStyle1 },
        { dataKey: "issued", title: "Fabric Issued", headerStyle: headerStyle1 },
        { dataKey: "assAllocDiff", title: "Fabric Left over W/H", headerStyle: headerStyle1 },

        { dataKey: "orderQty", title: "Order Qty", headerStyle: headerStyle2 },
        { dataKey: "orderQtySizeBreakup", title: "Order Qty Size Breakup", headerStyle: headerStyle2 },
        { dataKey: "fpoQty", title: "FPO Qty", headerStyle: headerStyle2 },
        { dataKey: "plannedQty", title: "Forcast Qty", headerStyle: headerStyle2 },
        { dataKey: "plannedCutPercent", title: "Forcast Cut %", cellStyle: { dataFormat: "0.0%" }, headerStyle: headerStyle2 },
        { dataKey: "netCutQty", title: "Net Cut Quantity", headerStyle: headerStyle2 },
        { dataKey: "netCutQtySizeBreakup", title: "Net Cut Qty Size Breakup", headerStyle: headerStyle2 },
        { dataKey: "rejectedQty", title: "Rejected Qty", headerStyle: headerStyle2 },
        { dataKey: "netCutPercent", title: "Net Cut (%)", cellStyle: { dataFormat: "0.0%" }, headerStyle: headerStyle2 },
        { dataKey: "lowCutPercentReason", title: "Reason for Short Forecast Qty", headerStyle: headerStyle2 },
        { dataKey: "shortShipPercent", title: "Buyer Min Tolerance", headerStyle: headerStyle2 },
        { dataKey: "extra", title: "Buyer Max Tolerance", headerStyle: headerStyle2 },

        { dataKey: "netIssued", title: "Net Issued", headerStyle: headerStyle1 },
        { dataKey: "shortageExcess", title: "Shortage/Excess", headerStyle: headerStyle1 },
        { dataKey: "usedInMarker", title: "Used in Marker", headerStyle: headerStyle1 },
        { dataKey: "endLoss", title: "End Loss", headerStyle: headerStyle1 },
        { dataKey: "usedInLayManual", title: "Used in Recutting Lay", headerStyle: headerStyle1 },
        { dataKey: "panelRecutResQty", title: "Estimated Recut % (from FCR)", headerStyle: headerStyle1 },
        { dataKey: "usedInPanelRemnant", title: "Panel Recut (End Bits)", headerStyle: headerStyle1 },
        { dataKey: "usedInPanelRoll", title: "Panel Recut (Roll)", headerStyle: headerStyle1 },
        { dataKey: "usedInPanel", title: "Total Panel Recut", headerStyle: headerStyle1 },
        { dataKey: "usedInPanelPercent", title: "Total Panel Recut %", cellStyle: { dataFormat: "0.00%" }, headerStyle: headerStyle1 },
        { dataKey: "higherRecut", title: "Reason for Higher Re-cut", headerStyle: headerStyle1 },
        { dataKey: "grossEndBits", title: "Gross End Bits", headerStyle: headerStyle1 },
        { dataKey: "grossEndBitsPercent", title: "Gross End Bits %", cellStyle: { dataFormat: "0.00%" }, headerStyle: headerStyle1 },
        { dataKey: "netEndBits", title: "Net End Bits", headerStyle: headerStyle1 },
        { dataKey: "higherEndBits", title: "Reason For Higher End Bits Leftover", headerStyle: headerStyle1 },
        { dataKey: "damaged", title: "Fabric Damaged", headerStyle: headerStyle1 },
        { dataKey: "netWastage", title: "Net Wastage", headerStyle: headerStyle1 },
        { dataKey: "missing", title: "Missed", headerStyle: headerStyle1 },
        { dataKey: "unused", title: "Unused", headerStyle: headerStyle1 },
        { dataKey: "unusedPercent", title: "Unused %", cellStyle: { dataFormat: "0.00%" }, headerStyle: headerStyle1 },
        { dataKey: "grossWastage", title: "Gross Wastage", headerStyle: headerStyle1 },
        { dataKey: "grossWastagePercent", title: "Gross Wastage %", cellStyle: { dataFormat: "0.00%" }, headerStyle: headerStyle1 },
        { dataKey: "wastageAboveTwoPercentMtrs", title: "Wastage above 2% value (meters) ", headerStyle: headerStyle1 },

        { dataKey: "returned", title: "Returned", headerStyle: headerStyle1 },
        { dataKey: "leftover", title: "Gross Leftover", headerStyle: headerStyle1 },
        { dataKey: "fabricLeftover", title: "Reason For Fabric Leftover factory", headerStyle: headerStyle1 },

        { dataKey: "totalConsumed", title: "Fabric Used For Cutting", headerStyle: headerStyle1 },
        { dataKey: "netConsumed", title: "Net Consumed", headerStyle: headerStyle1 },

        { dataKey: "orderingCu", title: "BOM CU", type: "double", cellStyle: { dataFormat: "0.000" }, headerStyle: headerStyle2 },
        { dataKey: "stdCu", title: "Std. CU", type: "double", cellStyle: { dataFormat: "0.000" }, headerStyle: headerStyle2 },
        { dataKey: "planCu", title: "Plan CU", type: "double", cellStyle: { dataFormat: "0.000" }, headerStyle: headerStyle2 },
        { dataKey: "actualCu", title: " Actual CU", type: "double", cellStyle: { dataFormat: "0.000" }, headerStyle: headerStyle2 },
        { dataKey: "grossCu", title: "Gross CU", cellStyle: { dataFormat: "0.000" }, headerStyle: headerStyle2 },
        { dataKey: "orderingCuFabReqd", title: "BOM CU Fabric Reqd", type: "double", cellStyle: { dataFormat: "0.000" }, headerStyle: headerStyle2 },
        { dataKey: "stdCuFabReqd", title: "Std. CU Fabric Reqd", type: "double", cellStyle: { dataFormat: "0.000" }, headerStyle: headerStyle2 },
        { dataKey: "actualCuFabReqd", title: "Actual CU Fabric Reqd", type: "double", cellStyle: { dataFormat: "0.000" }, headerStyle: headerStyle2 },
        { dataKey: "stdCuDiff", title: "BOM Vs Std  (CM)", type: "double", cellStyle: { dataFormat: "0.000" }, headerStyle: headerStyle2 },
        {
          dataKey: "stdCuDiffMtrs",
          title: "Diff - Mtrs (BOM vs std)",
          type: "double",
          cellStyle: { dataFormat: "0.000" },
          headerStyle: headerStyle2
        },
        { dataKey: "actualCuDiff", title: "BOM vs Actual (CM)", type: "double", cellStyle: { dataFormat: "0.0" }, headerStyle: headerStyle2 },
        { dataKey: "grossCuDiff", title: "BOM vs Gross (CM)", cellStyle: { dataFormat: "0.0" }, headerStyle: headerStyle2 },
        { dataKey: "stdActualCuDiff", title: "Std Vs Actual (CM)", type: "double", cellStyle: { dataFormat: "0.0" }, headerStyle: headerStyle2 },

        ...cuVarReasonList.map(c => ({ dataKey: c, title: `${c} - Mtrs`, headerStyle: headerStyle1 }))
      ].map(c => ({ ...c, title: lodashGet(i18nMessages, `resources.report.arr.excelColumns.${c.dataKey}`) || c.title }));
    }
    return cols;
  }, [reportId, alertParameterList]);

  const setFilter = useDeepCallback(
    values => {
      if (Object.keys(values).length === 0 || Object.keys(filterValues).length === 0) {
        initialize(`${reportId}-filter-form`, filterDefaultValues(defaultMonths)[dateKey]);
      } else {
        if (values["season"]) {
          const { orderCloseDate: dateValue, ...restValues } = values;
          changeFilterParams("reports", restValues);

          change(`${reportId}-filter-form`, "start", null);
          change(`${reportId}-filter-form`, "end", null);
        } else {
          values = { ...filterDefaultValues(defaultMonths), ...values };
          changeFilterParams("reports", values);
          const defaultValues = filterDefaultValues(defaultMonths)["orderCloseDate"] || {};
          if (filterValues && !filterValues.start) {
            change(`${reportId}-filter-form`, "start", defaultValues.start);
          }
          if (filterValues && !filterValues.end) {
            change(`${reportId}-filter-form`, "end", defaultValues.end);
          }
        }
      }
    },
    [filterValues]
  );

  const finalFilter = useDeepMemo(() => {
    if (isEmpty(filterValues)) return {};
    const { start, end, fabricUom, unitId, unitIds, line, customerIds, seasonId, productIds, supplierIds, fabricPatternIds, fabricCategoryIds } =
      filterValues || {};
    return {
      fabricUom,
      unitId,
      unitIds,
      line,
      customer: !isEmpty(customerIds) && !isEmpty(customers) ? customerIds.map(id => customers[id]?.name).filter(e => !isEmpty(e)) : [],
      season: seasonId && seasons[seasonId] && seasons[seasonId].name,
      product: !isEmpty(productIds) && !isEmpty(products) ? productIds.map(id => products[id]?.name).filter(e => !isEmpty(e)) : [],
      supplier: !isEmpty(supplierIds) && !isEmpty(suppliers) ? supplierIds.map(id => suppliers[id]?.name).filter(e => !isEmpty(e)) : [],
      fabricPattern:
        !isEmpty(fabricPatternIds) && !isEmpty(fabricPatterns) ? fabricPatternIds.map(id => fabricPatterns[id]?.name).filter(e => !isEmpty(e)) : [],
      fabricCategory:
        !isEmpty(fabricCategoryIds) && !isEmpty(fabricCategories)
          ? fabricCategoryIds.map(id => fabricCategories[id]?.name).filter(e => !isEmpty(e))
          : [],
      start: moment(start).startOf("day").valueOf(),
      end: moment(end).endOf("day").valueOf()
    };
  }, [filterValues, customers, seasons, products, suppliers, fabricPatterns, fabricCategories]);

  const searchText = getSearchText(finalFilter);

  const onDownload = () => {
    let options = {};
    if (reportId === "open-order") {
      options = {
        url: `custom-reports/${reportId}`,
        method: "post",
        data: {
          ...filterValues,
          start: typeof filterValues?.start === "string" ? Date.parse(filterValues?.start) : filterValues.start,
          end: typeof filterValues?.end === "string" ? Date.parse(filterValues?.end) : filterValues.end,
          unitIds: Array.isArray(filterValues?.unitIds)
            ? filterValues?.unitIds.filter(e => !isEmpty(e))
            : !isEmpty(filterValues?.unitIds)
              ? [filterValues.unitIds]
              : undefined
        }
      };
    } else if (reportId === "ocr") {
      options = {
        url: `report-summary`,
        method: "get",
        params: { search: searchText }
      };
    }

    mutate(options, {
      onSuccess: data => {
        if (isEmpty(data)) return;

        let fileName = "";
        if (reportId === "open-order") {
          fileName = `OPEN_ORDER_AGGREGATE__RECONCILIATION_REPORT.xlsx`;
        } else if (reportId === "ocr") {
          fileName = `CCR_REPORT.xlsx`;
        }

        let reportList = [];

        if (reportId === "open-order") {
          reportList = data
            .map(r => ({ ...r, rejectedQty: r.rejectedQty || 0 }))
            .map(r => {
              if (r.fabricUom === UOM.METER.value) {
                return {
                  ...r,
                  orderCloseDate: "NA",
                  netCutPercent: r.orderQty > 0 ? toFixedValue((r.cutQty - r.rejectedQty) / r.orderQty, 3) : 0.0,
                  actualCuDiff: (r.actualCu - r.orderingCu) * 100,
                  grossCuDiff: "NA",
                  grossCu: "NA",
                  returned: "NA",
                  leftover: toFixedValue(r.allocated + r.returned - r.issued, 1),
                  netIssued: toFixedValue(r.issued - r.returned, 1),
                  shortageExcess: r.shortage + r.excess,
                  usedInMarker: r.usedInLay - r.endLoss,
                  usedInPanelRemnant: r.grossEndBits - r.netEndBits,
                  usedInPanelRoll: r.usedInPanel - (r.grossEndBits - r.netEndBits),
                  netWastage: r.endLoss + r.netEndBits + r.usedInPanel + r.damaged,
                  grossWastage: r.endLoss + r.netEndBits + r.usedInPanel + r.damaged + r.missing + r.unused + r.blanketQty,
                  fabRepeat:
                    !isEmpty(r.fabricPattern) && r.fabricPattern?.toLowerCase() !== "solid"
                      ? isEmpty(r.repeatLength) && isEmpty(r.repeatWidth)
                        ? ""
                        : isEmpty(r.repeatLength)
                          ? `0 X ${r.repeatWidth}`
                          : isEmpty(r.repeatWidth)
                            ? `${r.repeatLength} X 0`
                            : `${r.repeatLength} X ${r.repeatWidth}`
                      : null,
                  markerEfficiency: toFixedValue(r.markerEfficiency / 100, 4),
                  widthDiff: toFixedValue(r.actualWidth - r.orderingWidth, 1)
                };
              } else {
                return {
                  ...r,
                  orderCloseDate: "NA",
                  orderingCu: r.orderingCuInKg,
                  stdCu: r.stdCuInKg,
                  planCu: r.planCuInKg,
                  actualCu: r.actualCuInKg,
                  grossCu: "NA",
                  netCutPercent: r.orderQty > 0 ? toFixedValue((r.cutQty - r.rejectedQty) / r.orderQty, 3) : 0.0,
                  actualCuDiff: (r.actualCuInKg - r.orderingCuInKg) * 1000,
                  grossCuDiff: "NA",
                  allocated: r.allocatedWeight,
                  blanketQty: r.blanketWeight,
                  issued: r.issuedWeight,
                  returned: "NA",
                  leftover: toFixedValue(r.allocatedWeight + r.returnedWeight - r.issuedWeight, 1),
                  netIssued: toFixedValue(r.issuedWeight - r.returnedWeight, 1),
                  shortageExcess: 0,
                  endLoss: r.endLossWeight,
                  grossEndBits: r.grossEndBitsWeight,
                  netEndBits: r.netEndBitsWeight,
                  usedInMarker: r.usedInLayWeight - r.endLossWeight,
                  usedInPanel: r.usedInPanelWeight,
                  damaged: r.damagedWeight,
                  missing: r.missingWeight,
                  unused: r.unusedWeight,
                  usedInPanelRemnant: r.grossEndBitsWeight - r.netEndBitsWeight,
                  usedInPanelRoll: r.usedInPanelWeight - (r.grossEndBitsWeight - r.netEndBitsWeight),
                  netWastage: r.endLossWeight + r.netEndBitsWeight + r.usedInPanelWeight + r.damagedWeight,
                  grossWastage:
                    r.endLossWeight + r.netEndBitsWeight + r.usedInPanelWeight + r.damagedWeight + r.missingWeight + r.unusedWeight + r.blanketWeight,
                  fabRepeat:
                    !isEmpty(r.fabricPattern) && r.fabricPattern?.toLowerCase() !== "solid"
                      ? isEmpty(r.repeatLength) && isEmpty(r.repeatWidth)
                        ? ""
                        : isEmpty(r.repeatLength)
                          ? `0 X ${r.repeatWidth}`
                          : isEmpty(r.repeatWidth)
                            ? `${r.repeatLength} X 0`
                            : `${r.repeatLength} X ${r.repeatWidth}`
                      : null,
                  markerEfficiency: toFixedValue(r.markerEfficiency / 100, 4),
                  widthDiff: toFixedValue(r.actualWidth - r.orderingWidth, 1)
                };
              }
            });
        } else if (reportId === "ocr") {
          const cutPlanQtyAlertParam = alertParameterList?.find(e => e.parameter === AlertParemeter.CUT_PLAN_QTY.id);
          const lowCutPercentReasonList = getDistinctValues(
            cutPlanQtyAlertParam?.alertParameterItems
              ?.filter(e => e.impact === "Decrease")
              ?.map(e => e.reason)
              ?.filter(e => !isEmpty(e)) || []
          );

          reportList = data
            .map(r => ({ ...r, rejectedQty: r.rejectedQty || 0 }))
            .map(r => {
              const fabricPattern = fabricPatterns?.find(fp => r.fabricPatternId === fp.id) || {};
              const highlightList = !isEmpty(r.highlights) ? JSON.parse(r.highlights) : [];

              const cutQtyReasonList = highlightList?.find(e => e.parameter === AlertParemeter.CUT_PLAN_QTY.id)?.reasons || [];
              const lowCutPercentReason = cutQtyReasonList?.filter(e => lowCutPercentReasonList?.includes(e))?.join(", ");

              const cuVarContribList = highlightList?.find(e => e.parameter === AlertParemeter.CONSUMPTION_VARIATION.id)?.contributionList || [];
              const contributions = cuVarContribList.reduce(
                (acc, c) => ({ ...acc, [c.reason]: !isEmpty(c.contribution) ? toFixed(c.contribution / 100, 4) : undefined }),
                {}
              );
              const fabricLeftover = highlightList?.find(h => h.parameter === "fabricLeftover")?.reasons?.join(",");
              const higherRecut = highlightList?.find(h => h.parameter === "higherRecut")?.reasons?.join(",");
              const higherEndBits = highlightList?.find(h => h.parameter === "higherEndBits")?.reasons?.join(",");
              const nominated = r.nominatedSupplier === true ? "Yes" : r.nominatedSupplier === false ? "No" : undefined;

              return {
                ...r,
                ...contributions,
                lowCutPercentReason,
                nominated,
                month: moment(r.cuttingClosedDate).format("MMMM, YY"),
                cuttingWeek: getCustomWeekNumber({
                  dateInput: r.cuttingClosedDate,
                  startMonth: financialYearStartMonth,
                  weekStartDay: weekStartDay
                }),
                warpShrinkageDiff: r.actualWarpShrinkage - r.orderingWarpShrinkage,
                weftShrinkageDiff: r.actualWeftShrinkage - r.orderingWeftShrinkage,
                fabricPattern: fabricPattern?.name,
                fabricLeftover,
                higherRecut,
                higherEndBits
              };
            })
            .map(r => {
              if (r.fabricUom === UOM.METER.value) {
                const shortageExcess = r.shortage + r.excess;
                const netIssued = r.issued - r.returned;
                const netConsumed = netIssued + r.blanketQty + shortageExcess;
                const grossWastage = r.endLoss + r.netEndBits + r.usedInPanel + r.damaged + r.missing + r.unused + r.blanketQty;
                const grossWastageFraction = netConsumed > 0 ? toFixedValue(grossWastage / netConsumed, 4) : 0.0;
                const grossWastagePercent = grossWastageFraction * 100;
                const wastageAboveTwoPercentMtrs = grossWastagePercent >= 2 ? (grossWastagePercent - 2) * 0.01 * netIssued : 0;

                const netCutQty = r.cutQty - r.rejectedQty;
                return {
                  ...r,
                  netCutQty: toFixedValue(netCutQty, 3),
                  plannedCutPercent: r.orderQty > 0 ? toFixedValue(r.plannedQty / r.orderQty, 3) : 0.0,
                  usedInPanelPercent: r.allocated > 0 ? toFixedValue(r.usedInPanel / r.allocated, 3) : 0.0,
                  grossEndBitsPercent: r.allocated > 0 ? toFixedValue(r.grossEndBits / r.allocated, 3) : 0.0,
                  unusedPercent: r.allocated > 0 ? toFixedValue(r.unused / r.allocated, 3) : 0.0,
                  netCutPercent: r.orderQty > 0 ? toFixedValue(netCutQty / r.orderQty, 3) : 0.0,
                  orderingCuFabReqd: toFixed(r.orderingCu * netCutQty, 3),
                  stdCuFabReqd: toFixed(r.stdCu * netCutQty, 3),
                  actualCuFabReqd: toFixed(r.actualCu * netCutQty, 3),
                  stdCuDiff: toFixedValue((r.stdCu - r.orderingCu) * 100, 3),
                  stdCuDiffMtrs: toFixedValue((r.stdCu - r.orderingCu) * netCutQty, 3),
                  actualCuDiff: (r.actualCu - r.orderingCu) * 100,
                  grossCuDiff: (r.grossCu - r.orderingCu) * 100,
                  stdActualCuDiff: (r.actualCu - r.stdCu) * 100,
                  leftover: toFixedValue(r.allocated + r.returned - r.issued, 3),
                  netIssued: toFixedValue(netIssued, 3),
                  shortageExcess: shortageExcess,
                  usedInMarker: r.usedInLay - r.endLoss,
                  usedInPanelRemnant: r.grossEndBits - r.netEndBits,
                  usedInPanelRoll: r.usedInPanel - (r.grossEndBits - r.netEndBits),
                  netWastage: r.endLoss + r.netEndBits + r.usedInPanel + r.damaged,
                  grossWastage: grossWastage,
                  grossWastagePercent: grossWastageFraction,
                  wastageAboveTwoPercentMtrs: toFixedValue(wastageAboveTwoPercentMtrs, 1),
                  panelRecutResQty: r.allocated > 0 ? toFixedValue(r.plannedReservedQty / r.allocated, 3) : 0.0,
                  fabRepeat:
                    !isEmpty(r.fabricPattern) && r.fabricPattern?.toLowerCase() !== "solid"
                      ? isEmpty(r.repeatLength) && isEmpty(r.repeatWidth)
                        ? ""
                        : isEmpty(r.repeatLength)
                          ? `0 X ${r.repeatWidth}`
                          : isEmpty(r.repeatWidth)
                            ? `${r.repeatLength} X 0`
                            : `${r.repeatLength} X ${r.repeatWidth}`
                      : null,
                  markerEfficiency: toFixedValue(r.markerEfficiency / 100, 4),
                  widthDiff: toFixedValue(r.actualWidth - r.orderingWidth, 1),
                  assAllocDiff: toFixedValue(r.associationQty - r.allocated, 1),
                  totalConsumed: toFixedValue(r.issued - r.returned + r.blanketQty, 1),
                  netConsumed: toFixedValue(netConsumed, 1)
                };
              } else {
                const shortageExcess = 0;
                const netIssued = r.issuedWeight - r.returnedWeight;
                const netConsumed = netIssued + r.blanketWeight + shortageExcess;
                const grossWastage =
                  r.endLossWeight + r.netEndBitsWeight + r.usedInPanelWeight + r.damagedWeight + r.missingWeight + r.unusedWeight + r.blanketWeight;
                const grossWastagePercent = netConsumed > 0 ? toFixedValue(grossWastage / netConsumed, 4) : 0.0;
                const grossWastPercent = grossWastagePercent * 100;
                const wastageAboveTwoPercentMtrs = grossWastPercent >= 2 ? (grossWastagePercent - 2) * netIssued : 0;

                const netCutQty = r.cutQty - r.rejectedQty;
                return {
                  ...r,
                  netCutQty: toFixedValue(netCutQty, 3),
                  orderingCu: r.orderingCuInKg,
                  stdCu: r.stdCuInKg,
                  planCu: r.planCuInKg,
                  actualCu: r.actualCuInKg,
                  plannedCutPercent: r.orderQty > 0 ? toFixedValue(r.plannedQty / r.orderQty, 3) : 0.0,
                  usedInPanelPercent: r.allocatedWeight > 0 ? toFixedValue(r.usedInPanelWeight / r.allocatedWeight, 3) : 0.0,
                  grossEndBitsPercent: r.allocatedWeight > 0 ? toFixedValue(r.grossEndBitsWeight / r.allocatedWeight, 3) : 0.0,
                  unusedPercent: r.allocatedWeight > 0 ? toFixedValue(r.unusedWeight / r.allocatedWeight, 3) : 0.0,
                  netCutPercent: r.orderQty > 0 ? toFixedValue(netCutQty / r.orderQty, 3) : 0.0,
                  orderingCuFabReqd: toFixed(r.orderingCuInKg * netCutQty, 3),
                  stdCuFabReqd: toFixed(r.stdCuInKg * netCutQty, 3),
                  actualCuFabReqd: toFixed(r.actualCuInKg * netCutQty, 3),
                  stdCuDiff: toFixedValue((r.stdCuInKg - r.orderingCuInKg) * 100, 3),
                  stdCuDiffMtrs: toFixedValue((r.stdCuInKg - r.orderingCuInKg) * netCutQty, 3),
                  actualCuDiff: (r.actualCuInKg - r.orderingCuInKg) * 1000,
                  grossCuDiff: (r.grossCu - r.orderingCu) * 100,
                  stdActualCuDiff: (r.actualCuInKg - r.stdCuInKg) * 100,
                  allocated: r.allocatedWeight,
                  blanketQty: r.blanketWeight,
                  issued: r.issuedWeight,
                  leftover: toFixedValue(r.allocatedWeight + r.returnedWeight - r.issuedWeight, 3),
                  netIssued: toFixedValue(netIssued, 3),
                  shortageExcess: 0,
                  endLoss: r.endLossWeight,
                  grossEndBits: r.grossEndBitsWeight,
                  netEndBits: r.netEndBitsWeight,
                  usedInMarker: r.usedInLayWeight - r.endLossWeight,
                  usedInPanel: r.usedInPanelWeight,
                  damaged: r.damagedWeight,
                  missing: r.missingWeight,
                  unused: r.unusedWeight,
                  usedInPanelRemnant: r.grossEndBitsWeight - r.netEndBitsWeight,
                  usedInPanelRoll: r.usedInPanelWeight - (r.grossEndBitsWeight - r.netEndBitsWeight),
                  netWastage: r.endLossWeight + r.netEndBitsWeight + r.usedInPanelWeight + r.damagedWeight,
                  grossWastage: grossWastage,
                  grossWastagePercent: grossWastagePercent,
                  wastageAboveTwoPercentMtrs: toFixedValue(wastageAboveTwoPercentMtrs, 1),
                  panelRecutResQty: r.allocated > 0 ? toFixedValue(r.plannedReservedWeight / r.allocated, 3) : 0.0,
                  fabRepeat:
                    !isEmpty(r.fabricPattern) && r.fabricPattern?.toLowerCase() !== "solid"
                      ? isEmpty(r.repeatLength) && isEmpty(r.repeatWidth)
                        ? ""
                        : isEmpty(r.repeatLength)
                          ? `0 X ${r.repeatWidth}`
                          : isEmpty(r.repeatWidth)
                            ? `${r.repeatLength} X 0`
                            : `${r.repeatLength} X ${r.repeatWidth}`
                      : null,
                  markerEfficiency: toFixedValue(r.markerEfficiency / 100, 4),
                  widthDiff: toFixedValue(r.actualWidth - r.orderingWidth, 1),
                  assAllocDiff: toFixedValue(r.associationWeight - r.allocatedWeight, 1),
                  totalConsumed: toFixedValue(r.issuedWeight - r.returnedWeight + r.blanketWeight, 1),
                  netConsumed: toFixedValue(netIssued + r.blanketWeight + shortageExcess, 1)
                };
              }
            });
        }

        const columnList = excelColumns.map((c, idx) => ({ ...c, colNo: idx + 1 }));
        const rowList = reportList.map((row, idx) => ({
          rowNo: idx + 1,
          cellList: columnList.map(c => ({
            dataKey: c.dataKey,
            value: row[c.dataKey]
          }))
        }));

        const sheetNo1 = {
          sheetNo: 0,
          sheetName: reportId === "ocr" ? "CCR Report" : "Open Order Reconciliation Report",
          excelData: {
            columnList,
            rowList
          }
        };

        const options = {
          url: `export/excel`,
          method: "post",
          responseType: "arraybuffer",
          data: [sheetNo1],
          params: { filename: fileName }
        };
        download(options, {
          onSuccess: response => {
            if (response.status === 200) {
              const header = response.headers["content-disposition"];
              var filename = header ? header.match(/filename="(.+)"/)[1] : fileName;

              let blob = new Blob([response.data], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              });
              let link = document.createElement("a");
              link.href = window.URL.createObjectURL(blob);
              link.download = filename;
              link.click();
            }
          },
          onFailure: err => {
            dispatch(showNotification(err.message));
          }
        });
      },
      onFailure: err => {
        dispatch(showNotification(err.message));
      }
    });
  };

  return (
    <div className={classes.root}>
      <PageHeader title={"Common Reports"} />
      {filterKeys.length > 0 && (
        <Drawer anchor="right" open={filterOpen} onClose={() => setFilterOpen(false)}>
          <Filter
            form={`${reportId}-filter-form`}
            parse={({
              start,
              end,
              fabricUom,
              unitId,
              unitIds,
              line,
              customerIds,
              seasonId,
              productIds,
              supplierIds,
              fabricPatternIds,
              fabricCategoryIds
            }) => {
              return {
                fabricUom,
                unitId,
                unitIds: !isEmpty(unitIds) ? `*${unitIds}*` : undefined,
                line,
                customer: customerIds && customerIds.map(id => customers[id] && customers[id].name),
                season: seasonId && seasons[seasonId] && seasons[seasonId].name,
                product: productIds && productIds.map(id => products[id] && products[id].name),
                supplier: supplierIds && supplierIds.map(id => suppliers[id] && suppliers[id].name),
                fabricPattern: fabricPatternIds && fabricPatternIds.map(id => fabricPatterns[id] && fabricPatterns[id].name),
                fabricCategory: fabricCategoryIds && fabricCategoryIds.map(id => fabricCategories[id] && fabricCategories[id].name),
                [dateKey]: {
                  start: moment(start).startOf("day").valueOf(),
                  end: moment(end).endOf("day").valueOf()
                }
              };
            }}
            initialValues={filterValues}
            setFilter={setFilter}
            translate={translate}>
            {filterKeys.includes("start") && <DateInput label="Planning Start Date" source="start" {...inputOptions} />}
            {filterKeys.includes("end") && <DateInput label="Planning End Date" source="end" {...inputOptions} />}
            {filterKeys.includes("fabricUom") && <SelectInput label="UOM" source="fabricUom" choices={uomChoices} {...inputOptions} />}
            {filterKeys.includes("unit") && <SelectInput label="Unit" source="unitId" choices={unitChoices} {...inputOptions} />}
            {filterKeys.includes("unitIds") && <SelectInput label="Unit" source="unitIds" choices={unitChoices} {...inputOptions} />}
            {filterKeys.includes("product") && (
              <ReferenceArrayInput
                label="Product"
                resource="products"
                source="productIds"
                reference="products"
                sort={{ field: "name", order: "asc" }}
                {...inputOptions}>
                <SelectArrayInput />
              </ReferenceArrayInput>
            )}
            {filterKeys.includes("season") && (
              <ReferenceInput
                label="Season"
                resource="seasons"
                source="seasonId"
                reference="seasons"
                pageSize={100}
                sort={{ field: "name", order: "asc" }}
                {...inputOptions}
                allowEmpty={true}>
                <SelectInput />
              </ReferenceInput>
            )}
            {filterKeys.includes("fabricPattern") && (
              <ReferenceArrayInput
                label="Fabric Type"
                resource="fabric-patterns"
                source="fabricPatternIds"
                reference="fabric-patterns"
                sort={{ field: "name", order: "asc" }}
                {...inputOptions}>
                <SelectArrayInput />
              </ReferenceArrayInput>
            )}
            {filterKeys.includes("fabricCategory") && (
              <ReferenceArrayInput
                label="Fabric Category"
                resource="fabric-categories"
                source="fabricCategoryIds"
                reference="fabric-categories"
                sort={{ field: "name", order: "asc" }}
                {...inputOptions}>
                <SelectArrayInput />
              </ReferenceArrayInput>
            )}
            {filterKeys.includes("supplier") && (
              <ReferenceArrayInput
                label="Mill"
                resource="suppliers"
                source="supplierIds"
                reference="suppliers"
                sort={{ field: "name", order: "asc" }}
                {...inputOptions}>
                <AutoCompleteArrayInput disablePortal={true} />
              </ReferenceArrayInput>
            )}
            {filterKeys.includes("customer") && (
              <ReferenceArrayInput
                label="Customer"
                resource="customers"
                source="customerIds"
                reference="customers"
                sort={{ field: "name", order: "asc" }}
                {...inputOptions}>
                <AutoCompleteArrayInput disablePortal={true} />
              </ReferenceArrayInput>
            )}
          </Filter>
          <div className={classes.displayCenter}>
            <Button label="Download" variant="contained" style={{ color: "green", marginLeft: "1em" }} onClick={onDownload}>
              {(loading || busy) && <CircularProgress size={20} thickness={2} />}
              {!loading && !busy && <ExcelIcon />}
            </Button>
          </div>
        </Drawer>
      )}

      <div className={classes.content}>
        <MuiTable rows={reports} columns={columns} inlineActions={inlineActions} onInlineActionClick={onInlineActionClick} fontSize={20} />
      </div>
    </div>
  );
};
export default withStyles(styles)(connect()(CommonReport));
